import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import PageHeader from "../../components/PageHeader/PageHeader";
import CommonTable from "../../components/CommonTable/CommonTable";
import { FaTimes, FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import "../Videos/Video.css";
import "./PromotionBanner.css";
import { deletes, get, patch, put } from "../../Services/ApiHandler";
import { BASE_URL, URLS } from "../../Services/Constant";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import { toast } from "react-toastify";

// backend may return a relative media path or a full URL
export const getBannerImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${BASE_URL}${path.replace(/^\//, "")}`;
};

const PromotionBanner = () => {
  const location = useLocation();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(location?.state?.page || 1);
  const [totalCount, setTotalCount] = useState();
  const [pagination, setPagination] = useState(null);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const pageRef = useRef(page);
  const preSearchPageRef = useRef(page);

  // keep pageRef in sync with the latest page
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  // clear state after using it (run only once when component mounts)
  useEffect(() => {
    if (location?.state?.page) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  const columns = useMemo(() => {
    let d = [
      {
        Header: "S.No",
        accessor: "sno",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <span className="sno">
              {String(row.index + 1 + (page - 1) * 10).padStart(2, "0")}
            </span>
          );
        },
      },
      {
        Header: "Banner Image",
        accessor: "image_url",
        disableSortBy: true,
        Cell: ({ value }) => {
          return value ? (
            <img
              src={getBannerImageUrl(value)}
              alt="banner"
              className="banner-thumb"
            />
          ) : (
            <p className="mb-0">N/A</p>
          );
        },
      },
      {
        Header: "Banner Name",
        accessor: "title",
        Cell: ({ value }) => {
          return <p className="mb-0">{value || "N/A"}</p>;
        },
      },
      {
        Header: "Status",
        accessor: "is_active",
        Cell: ({ value, row }) => {
          const getStatusClass = (status) => {
            if (status === true) return "status-active";
            if (status === false) return "status-inactive";
            return "";
          };

          return (
            <span
              className={`status-badge ${getStatusClass(value)}`}
              onClick={() => handleStatusChange(row.original)}
            >
              {value === true ? "Active" : "Inactive"}
            </span>
          );
        },
      },
    ];

    let action = {
      Header: "Action",
      accessor: "action",
      disableSortBy: true,
      Cell: ({ row }) => {
        return (
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              className="action-btn"
              onClick={() => handleView(row.original)}
            >
              <FaEye />
            </button>
            <button
              className="action-btn"
              onClick={() => handleEdit(row.original)}
            >
              <FaEdit />
            </button>
            <button
              className="action-btn"
              onClick={() => handleDelete(row.original)}
            >
              <FaTrash color="Red" />
            </button>
          </div>
        );
      },
    };

    return [...d, action];
  }, [page, data]);

  // view & edit share the same add page, only the mode differs
  const openBanner = (row, mode) => {
    navigate("/promotion-banner-add", {
      state: { data: row, mode, page: pageRef.current },
    });
  };
  const handleView = (row) => openBanner(row, "view");
  const handleEdit = (row) => openBanner(row, "edit");

  const handleClearSearch = () => {
    setSearchText("");
    setPage(preSearchPageRef.current || 1);
  };

  // avoid an API call on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchBanners = useCallback(async () => {
    let url = `${URLS.PROMOTION_BANNER_LIST}?search=${encodeURIComponent(
      debouncedSearch,
    )}`;
    if (!debouncedSearch) {
      url += `&page=${page || 1}`;
    }
    let res = await get(url);
    if (res?.status === "success") {
      setData(res?.data || []);
      setTotalCount(res?.count || 0);
      setPagination({
        current_page: res?.current_page || 1,
        last_page: res?.total_pages || 1,
      });
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  const handleStatusChange = async (row) => {
    try {
      if (!row?.id) return;
      let res = await patch(`${URLS.PROMOTION_BANNER_STATUS}${row.id}/`, {
        is_active: row.is_active === true ? false : true,
      });
      if (res?.status === "success") {
        toast.success(res?.message || "Status updated successfully");
        fetchBanners();
      }
    } catch (error) {
      console.error("Error updating banner status:", error);
    }
  };

  const confirmDelete = async () => {
    try {
      if (selectedBanner?.id) {
        let res = await deletes(
          URLS.PROMOTION_BANNER_DELETE + selectedBanner.id + "/",
        );
        if (res?.status === "success") {
          toast.success(res?.message || "Deleted successfully");
          fetchBanners();
        } else {
          toast.error(res?.message || "Failed to delete");
        }
        setIsDeleteModalOpen(false);
        setSelectedBanner(null);
      }
    } catch (error) {
      console.error("Error deleting banner:", error);
      toast.error(error?.message || "An error occurred");
    }
  };

  const handleDelete = (row) => {
    if (!row) return;
    setSelectedBanner(row);
    setIsDeleteModalOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Promotion Banner"
        buttonText="+Add Banner"
        buttonPath="/promotion-banner-add"
        skipPermission
        // permissionAllowed={"CAN-ADD-PROMOTION_BANNER"} change after permissions added
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h2 style={{ fontSize: "20px", fontWeight: "600", color: "#7a52ab" }}>
          Total: {totalCount || 0}
        </h2>
        <div className="search-wrapper">
          <input
            type="text"
            placeholder="Search banner name"
            className="search-input"
            value={searchText || ""}
            onChange={(e) => {
              const val = e.target.value;
              if (!searchText && val) {
                preSearchPageRef.current = page || 1;
                setPage(1);
              } else if (searchText && !val) {
                setPage(preSearchPageRef.current || 1);
              }
              setSearchText(val);
            }}
          />
          {searchText && (
            <FaTimes
              className="search-clear-icon"
              onClick={handleClearSearch}
            />
          )}
        </div>
      </div>
      <CommonTable
        columns={columns}
        data={data || []}
        pagecount={pagination}
        setPageno={setPage}
        pageno={page || 1}
      />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this banner?"
      />
    </div>
  );
};

export default PromotionBanner;
